import React, { useEffect, useState, useRef } from 'react';
import { formatTime } from '../utils/time';

function playChime() {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
        notes.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = 'sine';
            const start = ctx.currentTime + i * 0.15;
            gain.gain.setValueAtTime(0.3, start);
            gain.gain.exponentialRampToValueAtTime(0.01, start + 0.3);
            osc.start(start);
            osc.stop(start + 0.3);
        });
    } catch (e) {
        // Web Audio not available
    }
}

export default function StepBlock({ step, color, onAction, actionLabel, showTimer, showStartTimer, globalRemainingTime, showPrepTimer, onAdjustTime, defaultExpanded = false }) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [hasExpired, setHasExpired] = useState(false);
    const [prepTimeRemaining, setPrepTimeRemaining] = useState(null);
    const chimePlayed = useRef(false);

    // Reset chime when waiting_time changes (e.g. after +5m adjustment)
    useEffect(() => {
        if (showTimer && timeRemaining !== null && timeRemaining > 30) {
            chimePlayed.current = false;
        }
    }, [showTimer, step.waiting_time, timeRemaining]);

    // Calculate time remaining for waiting steps
    useEffect(() => {
        if (showTimer && step.prepped_at) {
            if (step.waiting_time === 0) {
                setTimeRemaining(0);
                setHasExpired(true);
                return;
            }

            const updateTimer = () => {
                const preppedTime = new Date(step.prepped_at).getTime();
                const now = Date.now();
                const elapsed = (now - preppedTime) / 1000;
                const remaining = Math.max(0, step.waiting_time - elapsed);
                setTimeRemaining(remaining);

                if (remaining === 0 && !hasExpired) {
                    setHasExpired(true);
                }

                if (remaining <= 30 && remaining > 0 && !chimePlayed.current) {
                    chimePlayed.current = true;
                    playChime();
                }
            };

            updateTimer();
            const interval = setInterval(updateTimer, 1000);
            return () => clearInterval(interval);
        }
    }, [showTimer, step.prepped_at, step.waiting_time, hasExpired]);

    // Derive start countdown directly from dynamic globalRemainingTime prop
    // (parent recomputes this every second accounting for overruns)
    const startCountdown = (showStartTimer && globalRemainingTime !== undefined)
        ? Math.max(0, Math.floor(globalRemainingTime - step.time_until_end))
        : null;

    // Calculate preparation time remaining for started steps
    useEffect(() => {
        if (showPrepTimer && step.started_at && step.preparation_time > 0) {
            const updatePrepTimer = () => {
                const startedTime = new Date(step.started_at).getTime();
                const now = Date.now();
                const elapsed = (now - startedTime) / 1000;
                const remaining = Math.max(0, step.preparation_time - elapsed);
                setPrepTimeRemaining(remaining);
            };

            updatePrepTimer();
            const interval = setInterval(updatePrepTimer, 1000);
            return () => clearInterval(interval);
        }
    }, [showPrepTimer, step.started_at, step.preparation_time]);

    // Determine which timer value to show in the collapsed row
    const getDisplayTimer = () => {
        if (showStartTimer && startCountdown !== null) {
            if (startCountdown === 0) return { label: 'Start now!', value: null, urgent: true };
            return { label: 'start in', value: formatTime(startCountdown), urgent: false };
        }
        if (showPrepTimer && prepTimeRemaining !== null) {
            return { label: 'prep', value: formatTime(prepTimeRemaining), urgent: false };
        }
        if (showTimer && timeRemaining !== null) {
            return { label: 'wait', value: formatTime(timeRemaining), urgent: timeRemaining === 0 };
        }
        return null;
    };

    const displayTimer = getDisplayTimer();

    return (
        <div className={`step-row step-row-${color}${expanded ? ' expanded' : ''}`}>
            <div className="step-row-header" onClick={() => setExpanded(!expanded)}>
                <div className="step-row-description">{step.title || step.description}</div>
                {displayTimer && (
                    <div className={`step-row-timer${displayTimer.urgent ? ' urgent' : ''}`}>
                        {displayTimer.value ? (
                            <span>{displayTimer.value}</span>
                        ) : (
                            <span>{displayTimer.label}</span>
                        )}
                    </div>
                )}
                {onAdjustTime && (
                    <div className="step-row-adjust">
                        <button className="adjust-btn" onClick={(e) => { e.stopPropagation(); onAdjustTime(-300); }}>-5m</button>
                        <button className="adjust-btn" onClick={(e) => { e.stopPropagation(); onAdjustTime(300); }}>+5m</button>
                    </div>
                )}
                {onAction && actionLabel && (
                    <button
                        className="step-row-action"
                        onClick={(e) => { e.stopPropagation(); onAction(); }}
                    >
                        {actionLabel}
                    </button>
                )}
            </div>

            {expanded && (
                <div className="step-row-details">
                    {step.description && (
                        <p className="step-description">{step.description}</p>
                    )}
                    {step.ingredients && step.ingredients.length > 0 && (
                        <div className="step-ingredients">
                            <strong>Ingredients:</strong>
                            <ul>
                                {step.ingredients.map((ing, idx) => (
                                    <li key={idx}>
                                        {ing.amount} {ing.unit} {ing.name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="step-times">
                        {step.preparation_time > 0 && (
                            <div className="time-info">
                                <strong>Prep time:</strong> {Math.floor(step.preparation_time / 60)}m
                            </div>
                        )}
                        {step.waiting_time > 0 && (
                            <div className="time-info">
                                <strong>Wait time:</strong> {Math.floor(step.waiting_time / 60)}m
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
